import { Component, computed, DestroyRef, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, of } from 'rxjs';

import {
  AdminPaymentRequestsFilter,
  AdminPaymentRequestFilters,
} from './payment-requests-filter/payment-requests-filter';
import { PaymentRequestsTable, LoadState } from './payment-requests-table/payment-requests-table';
import { PaymentRequestItem } from './payment-requests-table/payment-requests-table.module';
import { OrderActionPopup } from '../../../../shared/ui/order-action-popup/order-action-popup';

import {
  PaymentRequestService,
  GetPaymentRequestsParams,
  PaymentOrderByType,
  RejectPaymentRequestErrorResponse,
  mapApiItemToPaymentRequestItem,
  ApprovePaymentRequestErrorResponse,
} from '../../../../services/payment-request/payment-request.service';

import { PharmacyService } from '../../../../services/pharmacy/pharmacy.service';
import { LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { RequestModificationPopup } from '../../Payment-Request-Details/request-modification-popup/request-modification-popup';
import { PaymentRequestActions } from '../../../../services/payment-request/payment-request-actions.service';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';

const ORDER_BY_MAP: Record<string, PaymentOrderByType> = {
  CREATION_DATE: 'CREATION_DATE',
  AMOUNT: 'TOTAL_CASH',
};

const REJECT_ACTION_ID = 3;
const MODIFY_ACTION_ID = 4;
const EDIT_ACTION_ID = 5;
const CANCEL_ACTION_ID = 6;

@Component({
  selector: 'app-payment-requests-list-component',
  standalone: true,
  imports: [
    CommonModule,
    AdminPaymentRequestsFilter,
    PaymentRequestsTable,
    RequestModificationPopup,
    OrderActionPopup,
    SuccessfulNotification,
  ],
  templateUrl: './payment-requests-list-component.html',
  styleUrl: './payment-requests-list-component.scss',
})
export class PaymentRequestsListComponent implements OnInit {
  private router = inject(Router);
  private paymentRequestService = inject(PaymentRequestService);
  private paymentActions = inject(PaymentRequestActions);
  private pharmacyService = inject(PharmacyService);
  private localeId = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);

  isArabic = computed(() => this.localeId.startsWith('ar'));

  state = signal<LoadState>('loading');
  items = signal<PaymentRequestItem[]>([]);

  paymentStatuses = signal<LookupVTO[]>([]);

  statusOptionsForFilter = computed(() =>
    this.paymentStatuses().map((s) => ({
      id: s.id.toString(),
      titleEn: s.titleEn,
      titleAr: s.titleAr ?? '',
    })),
  );

  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(25);
  totalItems = signal<number>(0);
  totalPages = signal<number>(1);

  filters = signal<AdminPaymentRequestFilters>({
    orderBy: 'CREATION_DATE',
    orderDir: 'DESC',
  });

  isFiltersApplied = computed(() => {
    const f = this.filters();

    return !!(
      f.requestId ||
      f.pharmacyId ||
      f.createdBy ||
      (f.paymentMethodIds && f.paymentMethodIds.length > 0) ||
      (f.statusIds && f.statusIds.length > 0) ||
      f.createdOnFrom ||
      f.createdOnTo
    );
  });

  showRejectPopup = signal(false);
  rejectPopupEntity = signal<PaymentRequestItem | null>(null);
  rejectPopupLoading = signal(false);
  rejectErr = signal<string | undefined>(undefined);
  rejectSuccess = signal(false);

  showApprovePopup = signal(false);
  approvePopupEntity = signal<PaymentRequestItem | null>(null);
  approvePopupLoading = signal(false);
  approveErr = signal<string | undefined>(undefined);

  showCancelPopup = signal(false);
  cancelPopupEntity = signal<PaymentRequestItem | null>(null);
  cancelPopupLoading = signal(false);
  cancelErr = signal<string | undefined>(undefined);
  cancelSuccess = signal(false);
  cancelledRequestId = signal<string | number | null>(null);

  isRequestModOpen = signal<boolean>(false);
  selectedRequestId = signal<string | number | null>(null);

  ngOnInit(): void {
    this.loadPaymentStatuses();
    this.loadData();
  }

  loadPaymentStatuses(): void {
    const sub = this.pharmacyService.getAllStatuses(6001).subscribe({
      next: (res) => {
        this.paymentStatuses.set(res.list);
      },
      error: (err) => {
        console.error('Failed to load payment statuses', err);
      },
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  loadData(): void {
    this.state.set('loading');

    const params = this.buildApiParams();

    const sub = this.paymentRequestService
      .getAllPaymentRequests(params)
      .pipe(
        catchError((err) => {
          console.error('Payment Requests API Error:', err);
          this.state.set('error');
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (!response) return;

        this.items.set(response.list.map(mapApiItemToPaymentRequestItem));
        this.totalItems.set(response.total);
        this.totalPages.set(Math.max(1, Math.ceil(response.total / this.itemsPerPage())));
        this.state.set('success');
      });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  private formatDateForApi(dateStr?: string): string | undefined {
    if (!dateStr) return undefined;

    const d = new Date(dateStr);

    if (isNaN(d.getTime())) return undefined;

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private buildApiParams(): GetPaymentRequestsParams {
    const f = this.filters();

    const firstStatusId = f.statusIds?.[0];
    const requestStatusId = firstStatusId ? Number(firstStatusId) : undefined;

    const firstPaymentMethodId = f.paymentMethodIds?.[0];
    const paymentTypeId = firstPaymentMethodId ? Number(firstPaymentMethodId) : undefined;

    const createdById = f.createdById
      ? Number(f.createdById)
      : f.createdBy && !isNaN(Number(f.createdBy))
        ? Number(f.createdBy)
        : undefined;

    return {
      paymentId: f.requestId ? Number(f.requestId) : undefined,
      pharmacyId: f.pharmacyId ? Number(f.pharmacyId) : undefined,
      createdById,
      requestStatusId,
      paymentTypeId,
      createdOnFrom: this.formatDateForApi(f.createdOnFrom),
      createdOnTo: this.formatDateForApi(f.createdOnTo),
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
      orderBy: f.orderBy ? ORDER_BY_MAP[f.orderBy] || 'CREATION_DATE' : 'CREATION_DATE',
      orderDir: f.orderDir || 'DESC',
    };
  }

  onApplyFilters(newFilters: AdminPaymentRequestFilters): void {
    this.filters.set({
      orderBy: this.filters().orderBy,
      orderDir: this.filters().orderDir,
      ...newFilters,
    });

    this.currentPage.set(1);
    this.loadData();
  }

  onResetFilters(): void {
    this.filters.set({
      orderBy: 'CREATION_DATE',
      orderDir: 'DESC',
    });

    this.currentPage.set(1);
    this.loadData();
  }

  onToggleSortOrder(): void {
    this.filters.update((prev) => ({
      ...prev,
      orderDir: prev.orderDir === 'ASC' ? 'DESC' : 'ASC',
    }));

    this.loadData();
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.loadData();
  }

  onPageSizeChange(size: number): void {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
    this.loadData();
  }

  onRowClick(id: string | number): void {
    this.router.navigate(['/admin/payment-requests/view-payment-request-details', id]);
  }

  onActionTrigger(e: { event: Event; actionId: number; entity: any }): void {
    e.event?.stopPropagation();

    const { actionId, entity } = e;
    this.selectedRequestId.set(entity.id);

    switch (actionId) {
      case 1:
        this.onRowClick(entity.id);
        break;

      case 2:
        this.approvePopupEntity.set(entity);
        this.approveErr.set(undefined);
        this.showApprovePopup.set(true);
        break;

      case REJECT_ACTION_ID:
        this.rejectPopupEntity.set(entity);
        this.rejectErr.set(undefined);
        this.showRejectPopup.set(true);
        break;

      case MODIFY_ACTION_ID:
        this.isRequestModOpen.set(true);
        break;

      case EDIT_ACTION_ID:
        this.router.navigate(['/admin/payment-requests/modify-payment-request', entity.id], {
          state: { entity },
        });
        break;

      case CANCEL_ACTION_ID:
        this.cancelPopupEntity.set(entity);
        this.cancelErr.set(undefined);
        this.showCancelPopup.set(true);
        break;

      default:
        break;
    }
  }

  onModalSuccess(): void {
    this.loadData();
  }

  onRejectPopupClose(): void {
    if (this.rejectPopupLoading()) return;

    this.showRejectPopup.set(false);
    this.rejectPopupEntity.set(null);
    this.rejectErr.set(undefined);
  }

  onApprovePopupClose(): void {
    if (this.approvePopupLoading()) return;

    this.showApprovePopup.set(false);
    this.approvePopupEntity.set(null);
    this.approveErr.set(undefined);
  }

  onCancelPopupClose(): void {
    if (this.cancelPopupLoading()) return;

    this.showCancelPopup.set(false);
    this.cancelPopupEntity.set(null);
    this.cancelErr.set(undefined);
  }

  onRejectConfirmed(payload: { reasonId: number; otherReason: string; comment: string }): void {
    const entity = this.rejectPopupEntity();

    if (!entity) return;

    this.rejectPopupLoading.set(true);
    this.rejectErr.set(undefined);

    const trimmedComment = payload.comment?.trim() || '';

    const sub = this.paymentRequestService.rejectPaymentRequest(Number(entity.id), trimmedComment).subscribe({
      next: () => {
        this.rejectPopupLoading.set(false);
        this.onRejectPopupClose();
        this.rejectSuccess.set(true);
        this.loadData();
        setTimeout(() => this.rejectSuccess.set(false), 3000);
      },
      error: (err) => {
        console.error('Reject Payment Request API Error:', err);
        this.rejectPopupLoading.set(false);

        const apiError: RejectPaymentRequestErrorResponse | undefined = err?.error;

        this.rejectErr.set(
          (this.isArabic() ? apiError?.messageAr : apiError?.messageEn) ||
            (this.isArabic() ? 'حدث خطأ أثناء رفض الطلب' : 'Something went wrong while rejecting the request'),
        );
      },
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  onApproveConfirmed(payload: { comment: string }): void {
    const entity = this.approvePopupEntity();

    if (!entity) return;

    this.approvePopupLoading.set(true);
    this.approveErr.set(undefined);

    const sub = this.paymentRequestService.approvePaymentRequest(Number(entity.id), payload.comment).subscribe({
      next: () => {
        this.approvePopupLoading.set(false);
        this.onApprovePopupClose();
        this.loadData();
      },
      error: (err) => {
        console.error('Approve Payment Request API Error:', err);

        this.approvePopupLoading.set(false);

        const apiError: ApprovePaymentRequestErrorResponse | undefined = err?.error;

        this.approveErr.set(
          (this.isArabic() ? apiError?.messageAr : apiError?.messageEn) ||
            (this.isArabic() ? 'حدث خطأ أثناء اعتماد الطلب' : 'Something went wrong while approving the request'),
        );
      },
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  onCancelConfirmed(payload: { comment: string }): void {
    const entity = this.cancelPopupEntity();

    if (!entity) return;

    this.cancelPopupLoading.set(true);
    this.cancelErr.set(undefined);

    const comment = payload.comment?.trim();

    const sub = this.paymentActions
      .cancelPaymentRequest(Number(entity.id), { comment: comment || undefined })
      .subscribe({
        next: () => {
          this.cancelPopupLoading.set(false);
          this.cancelledRequestId.set(entity.id);
          this.onCancelPopupClose();
          this.cancelSuccess.set(true);
          this.loadData();
          setTimeout(() => this.cancelSuccess.set(false), 3000);
        },
        error: (err) => {
          this.cancelPopupLoading.set(false);

          this.cancelErr.set(
            (this.isArabic() ? err?.messageAr : err?.messageEn) ||
              (this.isArabic() ? 'حدث خطأ أثناء إلغاء الطلب' : 'Something went wrong while cancelling the request'),
          );
        },
      });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  onRequestModificationConfirmed(payload: { comment: string }): void {
    const entity = this.rejectPopupEntity();

    if (!entity) return;

    this.onRejectPopupClose();
    this.isRequestModOpen.set(true);
  }

  onCreateNewPayment(): void {
    this.router.navigate(['/admin/payment-requests/create']);
  }
}
